import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { RESOURCE_LIMIT_EXCEEDED_MESSAGE } from 'constants/errorMessages';
import { REGION_SUBSCRIPTION_PLANS } from 'constants/routes';
import { useContext } from 'react';
import { useAuthStore } from 'stores/authStore';
import { openUpgradeTierModal } from 'utils/handleTierExceededError';
import { applyMovePayload } from 'utils/reorder';
import LOCATIONS_KEYS from '../locations.keys';
import LOCATIONS_MUTATIONS from '../locations.mutation';
import LOCATIONS_QUERIES from '../locations.queries';

// LOCATION GROUPS

export const useFetchLocationGroups = () =>
  useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_GROUPS(),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_GROUPS(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchLocationGroupDetail = (uid) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_GROUPS_DETAIL(uid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_GROUP_DETAIL(uid),
    enabled: !!uid,
    staleTime: 1 * 60 * 1000, // 1 minute
    initialData: () =>
      queryClient.getQueryData(LOCATIONS_KEYS.LOCATION_GROUPS())?.find((g) => g.uid === uid),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(LOCATIONS_KEYS.LOCATION_GROUPS())?.dataUpdatedAt,
  });
};

export const useFetchAvailableLocations = () =>
  useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_GROUPS_AVAILABLE_LOCATIONS(),
    queryFn: () => LOCATIONS_QUERIES.FETCH_AVAILABLE_LOCATIONS(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateOrUpdateLocationGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.CREATE_OR_UPDATE_LOCATION_GROUP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION_GROUPS() });
    },
  });
};

export const useDeleteLocationGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.DELETE_LOCATION_GROUP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION_GROUPS() });
    },
  });
};

// LOCATIONS

export const useFetchLocations = (regionUid, options = {}) =>
  useQuery({
    queryKey: LOCATIONS_KEYS.LOCATIONS_LIST(regionUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATIONS(regionUid),
    staleTime: 1 * 60 * 1000, // 1 minute
    ...options,
  });

export const useFetchLocation = (locationUid, regionUid) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION(locationUid),
    enabled: !!locationUid,
    staleTime: 1 * 60 * 1000, // 1 minute
    initialData: () =>
      queryClient
        .getQueryData(LOCATIONS_KEYS.LOCATIONS_LIST(regionUid))
        ?.find((l) => l.uid === locationUid),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(LOCATIONS_KEYS.LOCATIONS_LIST(regionUid))?.dataUpdatedAt,
  });
};

export const useFetchRetailerLocation = (locationUid) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_RETAILER_LOCATION(locationUid),
    enabled: !!locationUid,
    staleTime: 1 * 60 * 1000, // 1 minute
    initialData: () =>
      queryClient.getQueryData(LOCATIONS_KEYS.LOCATIONS_LIST())?.find((l) => l.uid === locationUid),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(LOCATIONS_KEYS.LOCATIONS_LIST())?.dataUpdatedAt,
  });
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.CREATE_LOCATION,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS() });
      closePopup?.();
    },
    onError: (error, variables) => {
      if (error.message === RESOURCE_LIMIT_EXCEEDED_MESSAGE) {
        closePopup?.();

        const selectedRegion = variables.regionUid;
        const limit = user?.admin_details?.brand.regions.find(
          (region) => region.uid === selectedRegion,
        ).subscription.max_locations;
        const upgradeUrl = REGION_SUBSCRIPTION_PLANS.replace(':id', selectedRegion);

        openUpgradeTierModal({
          resourceType: 'locations',
          currentLimit: limit,
          upgradeUrl,
        });
      }
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.UPDATE_LOCATION,
    onSuccess: (data, variables) => {
      if (variables.skipInvalidation) return;

      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS_LISTS() });
      queryClient.invalidateQueries({
        queryKey: LOCATIONS_KEYS.LOCATION(variables.uid),
        exact: true,
      });
    },
  });
};

export const useUpdateLocationAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.UPDATE_LOCATION_ADDRESS,
    onSuccess: (data, variables) => {
      if (variables.skipInvalidation) return;

      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS_LISTS() });
      queryClient.invalidateQueries({
        queryKey: LOCATIONS_KEYS.LOCATION(variables.uid),
        exact: true,
      });
    },
  });
};

export const useUpdateLocationOpeningHours = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.UPDATE_LOCATION_OPENING_HOURS,
    onSuccess: (data, variables) => {
      if (variables.skipInvalidation) return;

      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS_LISTS() });
      queryClient.invalidateQueries({
        queryKey: LOCATIONS_KEYS.LOCATION(variables.uid),
        exact: true,
      });
    },
  });
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.DELETE_LOCATION,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS() });
    },
  });
};

// LOCATION PARTICIPANTS

export const useFetchLocationAvailableUsers = (locationUid) =>
  useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_USERS(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_USERS(locationUid),
    enabled: !!locationUid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchLocationAvailableLooks = (locationUid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_LOOKS(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_LOOKS(locationUid),
    enabled: !!locationUid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchLocationAvailableBanners = (locationUid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_BANNERS(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_BANNERS(locationUid),
    enabled: !!locationUid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchLocationAvailableVideos = (locationUid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_VIDEOS(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_VIDEOS(locationUid),
    enabled: !!locationUid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchLocationAvailableParticipants = (locationUid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_PARTICIPANTS(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_PARTICIPANTS(locationUid),
    enabled: !!locationUid && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    ...queryOptions,
  });
};

export const useFetchLocationAvailableEvents = (locationUid, options = {}) => {
  const { enabled = true, ...queryOptions } = options;

  return useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_EVENTS(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_EVENTS(locationUid),
    enabled: !!locationUid && enabled,
    staleTime: 1 * 60 * 1000,
    ...queryOptions,
  });
};

export const useAddLocationParticipant = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.ADD_LOCATION_PARTICIPANT,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION(variables.locationUid) });
      queryClient.invalidateQueries({
        queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_USERS(variables.locationUid),
      });
      closePopup?.();
    },
  });
};

export const useRemoveLocationParticipant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.REMOVE_LOCATION_PARTICIPANT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS() });
    },
  });
};

export const useUpdateLocationParticipant = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.UPDATE_LOCATION_PARTICIPANT,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS() });
      closePopup?.();
    },
  });
};

// LOCATION MODULES

export const useFetchLocationAvailableModulesContent = (locationUid) =>
  useQuery({
    queryKey: LOCATIONS_KEYS.LOCATION_AVAILABLE_MODULES(locationUid),
    queryFn: () => LOCATIONS_QUERIES.FETCH_LOCATION_AVAILABLE_MODULES_CONTENT(locationUid),
    enabled: !!locationUid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useAddLocationModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.ADD_LOCATION_MODULE,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION(variables.locationUid) });
    },
  });
};

export const useUpdateLocationModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.UPDATE_LOCATION_MODULE,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION(variables.locationUid) });
    },
  });
};

export const useUpdateLocationModuleOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationUid, move }) =>
      LOCATIONS_MUTATIONS.MOVE_LOCATION_MODULE({
        locationUid,
        ...move,
      }),
    onMutate: ({ locationUid, move }) => {
      const queryKey = LOCATIONS_KEYS.LOCATION(locationUid);
      queryClient.cancelQueries({ queryKey });

      const previousLocation = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old?.page_modules) return old;
        return {
          ...old,
          page_modules: applyMovePayload(old.page_modules, move),
        };
      });

      return { previousLocation };
    },
    onError: (_err, { locationUid }, context) => {
      if (context?.previousLocation) {
        queryClient.setQueryData(LOCATIONS_KEYS.LOCATION(locationUid), context.previousLocation);
      }
    },
    onSettled: (_data, _err, { locationUid }) => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION(locationUid) });
    },
  });
};

export const useDeleteLocationModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: LOCATIONS_MUTATIONS.DELETE_LOCATION_MODULE,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION(variables.locationUid) });
    },
  });
};
